import classNames from '../../../../../utils/classNames';
import type {
    TableCellObjectSettings,
    TableCellSettings,
    HorizontalAlignment,
    TableLayoutShape,
    TableLayoutRowSettings,
    TableSettings,
} from './contracts';

export function hasTableContent(shape: TableLayoutShape): boolean {
    return shape.rows.length > 0;
}

export function shouldRenderTable(
    settings: TableSettings,
    shape: TableLayoutShape,
    keepEmptyVisible: boolean
): boolean {
    return keepEmptyVisible || settings.showWhenEmpty || hasTableContent(shape);
}

const ALIGNMENT_CLASS_NAMES: Record<HorizontalAlignment, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

interface TableStyleClasses {
    wrapper: string;
    title: string;
    table: string;
    headerCell: string;
    bodyCell: string;
}

const CONDENSED_TABLE_STYLE_CLASSES: TableStyleClasses = {
    wrapper: 'mb-3 overflow-visible',
    title: 'mb-1 text-sm font-semibold text-slate-900',
    table: 'w-full border-collapse align-middle text-xs tabular-nums leading-tight',
    headerCell: 'border border-slate-300 px-1.5 py-1 font-semibold leading-tight text-slate-800',
    bodyCell: 'border border-slate-300 px-1.5 py-1 text-slate-800',
};

const SPACIOUS_TABLE_STYLE_CLASSES: TableStyleClasses = {
    wrapper: 'mb-4 overflow-visible',
    title: 'mb-2 text-base font-semibold text-slate-900',
    table: 'w-full border-collapse align-middle text-sm',
    headerCell: 'border border-slate-500 px-3 py-2 font-semibold text-slate-900',
    bodyCell: 'border border-slate-400 px-3 py-2 text-slate-800',
};

function getTableStyleClasses(settings: TableSettings): TableStyleClasses {
    return settings.spacious ? SPACIOUS_TABLE_STYLE_CLASSES : CONDENSED_TABLE_STYLE_CLASSES;
}

interface NormalizedTableCell extends TableCellObjectSettings {
    columnIndex: number;
}

function getColumnAlignmentClassName(alignment: HorizontalAlignment | undefined): string {
    return alignment ? ALIGNMENT_CLASS_NAMES[alignment] : ALIGNMENT_CLASS_NAMES.center;
}

function validateOptionalPositiveInteger(value: number | undefined, path: string): void {
    if (typeof value === 'undefined') {
        return;
    }

    if (!Number.isInteger(value) || value < 1) {
        throw new Error(`Expected "${path}" to be a positive integer.`);
    }
}

function validateOptionalColumnIndex(
    value: number | undefined,
    path: string,
    columnCount: number
): void {
    if (typeof value === 'undefined') {
        return;
    }

    if (!Number.isInteger(value) || value < 0 || value >= columnCount) {
        throw new Error(`Expected "${path}" to be a valid table column index.`);
    }
}

function normalizeTableCell(
    cell: TableCellSettings,
    path: string,
    columnCount: number
): TableCellObjectSettings {
    if (typeof cell === 'string') {
        return { value: cell };
    }

    validateOptionalPositiveInteger(cell.rowSpan, `${path}.rowSpan`);
    validateOptionalColumnIndex(cell.columnIndex, `${path}.columnIndex`, columnCount);

    return cell;
}

function normalizeRowCells(
    row: TableLayoutRowSettings,
    rowPath: string,
    columnCount: number
): NormalizedTableCell[] {
    const normalizedCells = row.cells.map((cell, cellIndex) => {
        const normalizedCell = normalizeTableCell(
            cell,
            `${rowPath}.cells[${cellIndex}]`,
            columnCount
        );

        return {
            ...normalizedCell,
            columnIndex: normalizedCell.columnIndex ?? cellIndex,
        };
    });

    normalizedCells.reduce((previousColumnIndex, cell, cellIndex) => {
        const { columnIndex } = cell;
        if (columnIndex <= previousColumnIndex) {
            throw new Error(
                `Expected "${rowPath}.cells[${cellIndex}].columnIndex" to be greater than the previous cell column index.`
            );
        }

        return columnIndex;
    }, -1);

    return normalizedCells;
}

function renderRow(
    row: TableLayoutRowSettings,
    rowIndex: number,
    columnAlignments: readonly HorizontalAlignment[] | undefined,
    columnCount: number,
    keyPrefix: string,
    styleClasses: TableStyleClasses
): JSX.Element {
    const normalizedCells = normalizeRowCells(row, `${keyPrefix}.rows[${rowIndex}]`, columnCount);

    return (
        <tr key={`${keyPrefix}-row-${rowIndex}`} className="break-inside-avoid">
            {normalizedCells.map((cell, cellIndex) => (
                <td
                    key={`${keyPrefix}-row-${rowIndex}-cell-${cellIndex}`}
                    rowSpan={cell.rowSpan}
                    className={classNames(
                        styleClasses.bodyCell,
                        cell.rowSpan ? 'align-top' : null,
                        getColumnAlignmentClassName(columnAlignments?.[cell.columnIndex])
                    )}
                >
                    {cell.value}
                </td>
            ))}
        </tr>
    );
}

/**
 * The rows a row span ties together. A spanning cell is rendered once, on the group's first
 * row, so a group split across a page break leaves the continuation rows on the next page with
 * that column blank. Each group is its own `tbody` so the break can be avoided for the complete
 * row span. Rows also avoid breaks individually because a group can be taller than one page.
 */
function groupRowsBySpan(
    rows: readonly TableLayoutRowSettings[]
): { startIndex: number; rows: readonly TableLayoutRowSettings[] }[] {
    const groups: { startIndex: number; rows: readonly TableLayoutRowSettings[] }[] = [];

    let startIndex = 0;
    while (startIndex < rows.length) {
        const span = Math.max(
            1,
            ...rows[startIndex].cells.map((cell) =>
                typeof cell === 'string' ? 1 : cell.rowSpan ?? 1
            )
        );

        const groupRows = rows.slice(startIndex, startIndex + span);
        if (groupRows.length !== span) {
            throw new Error(
                `Expected ${span} rows for the row span at row ${startIndex}, got ${groupRows.length}.`
            );
        }

        const nested = groupRows.findIndex(
            (row, offset) =>
                offset > 0 && row.cells.some((cell) => typeof cell !== 'string' && cell.rowSpan)
        );
        if (nested > 0) {
            throw new Error(`Unexpected nested row span at row ${startIndex + nested}.`);
        }

        groups.push({ startIndex, rows: groupRows });
        startIndex += span;
    }

    return groups;
}

function renderRowGroups(
    rows: readonly TableLayoutRowSettings[],
    columnAlignments: readonly HorizontalAlignment[] | undefined,
    columnCount: number,
    keyPrefix: string,
    styleClasses: TableStyleClasses
): JSX.Element[] {
    return groupRowsBySpan(rows).map((group) => (
        <tbody key={`${keyPrefix}-group-${group.startIndex}`} className="break-inside-avoid">
            {group.rows.map((row, offset) =>
                renderRow(
                    row,
                    group.startIndex + offset,
                    columnAlignments,
                    columnCount,
                    keyPrefix,
                    styleClasses
                )
            )}
        </tbody>
    ));
}

export function renderTableLayout(
    settings: TableSettings,
    renderedShape: TableLayoutShape
): JSX.Element {
    const styleClasses = getTableStyleClasses(settings);

    return (
        <div className={styleClasses.wrapper}>
            {settings.title ? <h3 className={styleClasses.title}>{settings.title}</h3> : null}
            <table className={styleClasses.table}>
                {renderedShape.columnWidths ? (
                    <colgroup>
                        {renderedShape.header.map((_name, columnIndex) => (
                            <col
                                key={`table-col-${columnIndex}`}
                                style={{
                                    width: renderedShape.columnWidths?.[columnIndex],
                                }}
                            />
                        ))}
                    </colgroup>
                ) : null}
                <thead className="[display:table-header-group]">
                    <tr>
                        {renderedShape.header.map((name, columnIndex) => (
                            <th
                                key={`table-header-${columnIndex}`}
                                className={classNames(
                                    styleClasses.headerCell,
                                    getColumnAlignmentClassName(
                                        renderedShape.columnAlignments?.[columnIndex]
                                    )
                                )}
                            >
                                {name}
                            </th>
                        ))}
                    </tr>
                </thead>
                {renderRowGroups(
                    renderedShape.rows,
                    renderedShape.columnAlignments,
                    renderedShape.header.length,
                    'table',
                    styleClasses
                )}
            </table>
        </div>
    );
}
